import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { partner } from '../api/partner';
import { toast } from '../app/toast';
import { APP_VERSION } from '../config';
import { useI18n, useT } from '../i18n';
import { describeError } from '../lib/errors';
import { buildContext, buildQualityItem, useScreenExtra } from '../lib/problemContext';
import { AlertIcon, BulbIcon } from './Icons';
import { Sheet } from './Sheet';

/** Floating «Проблема / ідея» on every authenticated screen → Quality workspace of VIA DOMUM. */
export function ProblemButton() {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <>
      {/* A strip of its own right above the tab bar (not a pill floating over the cards): content scrolls under the
          strip and the page has bottom padding for it, so the button never hides text. */}
      <div
        className="fixed inset-x-0 z-40 border-t border-line bg-bg/95 backdrop-blur"
        style={{ bottom: 'calc(var(--tabbar-h) + env(safe-area-inset-bottom, 0px))', height: 'var(--problem-h)' }}
      >
        <div className="page flex h-full items-center justify-end">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-line bg-card px-4 text-[15px] font-medium active:bg-hov"
          >
            <AlertIcon />
            {t('problem.button')}
          </button>
        </div>
      </div>
      {open ? <ProblemSheet onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function ProblemSheet({ onClose }: { onClose: () => void }) {
  const t = useT();
  const location = useLocation();
  const locale = useI18n((s) => s.locale);
  const extra = useScreenExtra((s) => s.extra);
  const [kind, setKind] = useState<'problem' | 'idea'>('problem');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [attach, setAttach] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) {
      setError(t('problem.required'));
      return;
    }
    setBusy(true);
    setError(null);
    const context = attach
      ? buildContext({
          pathname: location.pathname,
          url: window.location.href,
          appVersion: APP_VERSION,
          locale,
          userAgent: navigator.userAgent,
          extra,
        })
      : null;
    try {
      await partner.qualityItem(buildQualityItem(kind, title, body, context, attach ? extra.handover_id : undefined));
      toast(t('problem.thanks'));
      onClose();
    } catch (err) {
      setError(t(describeError(err)));
    } finally {
      setBusy(false);
    }
  }

  const kindBtn = (k: 'problem' | 'idea', icon: React.ReactNode) => (
    <button
      type="button"
      aria-pressed={kind === k}
      onClick={() => setKind(k)}
      className={`inline-flex min-h-tap flex-1 items-center justify-center gap-2 rounded-xl border text-[16px] font-medium ${kind === k ? 'border-fg bg-acc text-acc-fg' : 'border-line'}`}
    >
      {icon}
      {t(k === 'problem' ? 'problem.kind.problem' : 'problem.kind.idea')}
    </button>
  );

  return (
    <Sheet title={t('problem.title')} onClose={onClose}>
      <form onSubmit={send} className="space-y-4" noValidate>
        <fieldset>
          <legend className="label">{t('problem.kind')}</legend>
          <div className="flex gap-3">
            {kindBtn('problem', <AlertIcon />)}
            {kindBtn('idea', <BulbIcon />)}
          </div>
        </fieldset>
        <div>
          <label htmlFor="pb-title" className="label">
            {t('problem.subject')}
          </label>
          <input id="pb-title" className="input" value={title} maxLength={300} onChange={(e) => setTitle(e.target.value)} aria-invalid={!!error && !title.trim()} />
        </div>
        <div>
          <label htmlFor="pb-body" className="label">
            {t('problem.body')}
          </label>
          <textarea id="pb-body" className="input min-h-[110px]" value={body} maxLength={10000} onChange={(e) => setBody(e.target.value)} />
        </div>
        <label className="flex min-h-tap items-center gap-3 text-[15px]">
          <input type="checkbox" className="h-6 w-6 flex-none accent-current" checked={attach} onChange={(e) => setAttach(e.target.checked)} />
          {t('problem.attach')}
        </label>
        {error ? (
          <p role="alert" className="text-bad">
            {error}
          </p>
        ) : null}
        <button type="submit" className="btn-primary min-h-big w-full text-[18px]" disabled={busy}>
          {t('problem.send')}
        </button>
      </form>
    </Sheet>
  );
}
